import { useEffect, useState } from 'react';

/**
 * Whether the visitor has asked their operating system to reduce motion.
 *
 * Most of the site honours this purely in CSS via the `--motion-*` tokens.
 * This hook exists for the handful of places where JavaScript has to make the
 * decision — scroll behaviour, and skipping an animation entirely rather than
 * running it in 1ms.
 */
export function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) => setPrefers(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return prefers;
}

/** Sets document.title, and restores it on unmount. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
